import { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

function Analytics() {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMarketplace, setSelectedMarketplace] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedSku, setSelectedSku] = useState('all');

  // Dummy data for sales trend
  const allSalesData = {
    all: [
      { date: '2023-08-01', sales: 15000 },
      { date: '2023-08-05', sales: 18000 },
      { date: '2023-08-10', sales: 22000 },
      { date: '2023-08-15', sales: 25000 },
      { date: '2023-08-20', sales: 30000 },
      { date: '2023-08-25', sales: 28000 },
      { date: '2023-08-30', sales: 35000 },
    ],
    'amazon-uae': [
      { date: '2023-08-01', sales: 8000 },
      { date: '2023-08-05', sales: 10000 },
      { date: '2023-08-10', sales: 12000 },
      { date: '2023-08-15', sales: 15000 },
      { date: '2023-08-20', sales: 18000 },
      { date: '2023-08-25', sales: 16000 },
      { date: '2023-08-30', sales: 20000 },
    ],
    noon: [
      { date: '2023-08-01', sales: 4000 },
      { date: '2023-08-05', sales: 5000 },
      { date: '2023-08-10', sales: 6000 },
      { date: '2023-08-15', sales: 6500 },
      { date: '2023-08-20', sales: 7500 },
      { date: '2023-08-25', sales: 7000 },
      { date: '2023-08-30', sales: 9000 },
    ],
    jumia: [
      { date: '2023-08-01', sales: 2000 },
      { date: '2023-08-05', sales: 2200 },
      { date: '2023-08-10', sales: 2500 },
      { date: '2023-08-15', sales: 2700 },
      { date: '2023-08-20', sales: 3000 },
      { date: '2023-08-25', sales: 3200 },
      { date: '2023-08-30', sales: 4000 },
    ],
    carrefour: [
      { date: '2023-08-01', sales: 1000 },
      { date: '2023-08-05', sales: 800 },
      { date: '2023-08-10', sales: 1500 },
      { date: '2023-08-15', sales: 800 },
      { date: '2023-08-20', sales: 1500 },
      { date: '2023-08-25', sales: 1800 },
      { date: '2023-08-30', sales: 2000 },
    ],
  };

  // Get sales data based on selected marketplace
  const salesData = allSalesData[selectedMarketplace];

  // Dummy data for marketplace distribution
  const marketplaceData = [
    { name: 'Amazon UAE', value: 40, id: 'amazon-uae' },
    { name: 'Noon', value: 30, id: 'noon' },
    { name: 'Jumia', value: 20, id: 'jumia' },
    { name: 'Carrefour', value: 10, id: 'carrefour' },
  ];

  // Dummy data for top products
  const topProductsData = [
    { name: 'Product A', sales: 12500 },
    { name: 'Product B', sales: 9800 },
    { name: 'Product C', sales: 7600 },
    { name: 'Product D', sales: 6900 },
    { name: 'Product E', sales: 5400 },
  ];

  const COLORS = ['#4F46E5', '#818CF8', '#A5B4FC', '#C7D2FE'];

  const stats = {
    all: [
      { name: 'Total Sales', value: '$158,000', change: '+12.5%' },
      { name: 'Active Listings', value: '1,248', change: '+8.2%' },
      { name: 'Conversion Rate', value: '4.2%', change: '+2.1%' },
      { name: 'Avg. Order Value', value: '$125', change: '+5.4%' },
    ],
    'amazon-uae': [
      { name: 'Total Sales', value: '$82,000', change: '+15.2%' },
      { name: 'Active Listings', value: '856', change: '+10.1%' },
      { name: 'Conversion Rate', value: '4.8%', change: '+2.8%' },
      { name: 'Avg. Order Value', value: '$135', change: '+6.2%' },
    ],
    noon: [
      { name: 'Total Sales', value: '$45,000', change: '+9.8%' },
      { name: 'Active Listings', value: '245', change: '+5.5%' },
      { name: 'Conversion Rate', value: '3.9%', change: '+1.5%' },
      { name: 'Avg. Order Value', value: '$118', change: '+4.1%' },
    ],
    jumia: [
      { name: 'Total Sales', value: '$21,000', change: '+8.4%' },
      { name: 'Active Listings', value: '98', change: '+4.2%' },
      { name: 'Conversion Rate', value: '3.5%', change: '+1.2%' },
      { name: 'Avg. Order Value', value: '$105', change: '+3.8%' },
    ],
    carrefour: [
      { name: 'Total Sales', value: '$10,000', change: '+6.5%' },
      { name: 'Active Listings', value: '49', change: '+2.8%' },
      { name: 'Conversion Rate', value: '3.2%', change: '+0.9%' },
      { name: 'Avg. Order Value', value: '$95', change: '+2.5%' },
    ],
  };

  const currentStats = stats[selectedMarketplace];

  return (
    <div>
      <h1 className="text-4xl font-bold mb-6">Analytics Dashboard</h1>
      
      <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Marketplace
            </label>
            <select
              value={selectedMarketplace}
              onChange={(e) => setSelectedMarketplace(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="all">All Marketplaces</option>
              <option value="amazon-uae">Amazon UAE</option>
              <option value="noon">Noon</option>
              <option value="jumia">Jumia</option>
              <option value="carrefour">Carrefour</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="all">All Products</option>
              <option value="wireless-headphones">Wireless Headphones</option>
              <option value="smart-watch">Smart Watch</option>
              <option value="bluetooth-speaker">Bluetooth Speaker</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU
            </label>
            <select
              value={selectedSku}
              onChange={(e) => setSelectedSku(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="all">All SKUs</option>
              <option value="WH-001">WH-001</option>
              <option value="SW-001">SW-001</option>
              <option value="BS-001">BS-001</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Range
            </label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {currentStats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-xl p-6 shadow-sm">
            <p className="text-sm text-gray-500">{stat.name}</p>
            <p className="text-2xl font-bold mt-2">{stat.value}</p>
            <p className={`text-sm mt-2 ${stat.change.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
              {stat.change} vs. last period
            </p>
          </div>
        ))}
      </div>

      {/* Sales Trend Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
        <h2 className="text-xl font-bold mb-4">Sales Trend</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="sales" stroke="#4F46E5" fill="#818CF8" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Marketplace Distribution */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-4">Marketplace Distribution</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={marketplaceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label
                >
                  {marketplaceData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[index % COLORS.length]}
                      opacity={selectedMarketplace === 'all' || selectedMarketplace === entry.id ? 1 : 0.3}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-4">Top Products</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProductsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="sales" fill="#4F46E5" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;